'use client';

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  label: string;
  value: string;
  change: string;
  icon: string;
}

interface ChartData {
  name: string;
  before: number;
  after: number;
}

interface SuccessMetricsProps {
  metrics: Metric[];
  chartData: ChartData[];
}

const SuccessMetrics = ({ metrics, chartData }: SuccessMetricsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Bewezen resultaten
            </h2>
            <p className="text-lg text-muted-foreground">
              Meetbare impact op uw bedrijfsresultaten
            </p>
          </div>
          <div className="h-96 glassmorphic rounded-xl flex items-center justify-center shadow-silver-glow card-3d-glow">
            <div className="text-muted-foreground">Laden...</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Bewezen resultaten
          </h2>
          <p className="text-lg text-muted-foreground">
            Meetbare impact op uw bedrijfsresultaten
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="glassmorphic rounded-xl p-6 text-center shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow"
            >
              <div className="text-4xl font-display font-bold text-success mb-2 glow-success">
                {metric.value}
              </div>
              <div className="text-sm text-foreground font-semibold mb-2">
                {metric.label}
              </div>
              <div className="text-xs text-success font-semibold">
                {metric.change}
              </div>
            </div>
          ))}
        </div>
        
        <div className="glassmorphic rounded-xl p-8 shadow-silver-glow card-3d-glow mb-12">
          <h3 className="text-2xl font-display font-bold text-foreground mb-8 text-center">
            Voor & Na vergelijking
          </h3>
          
          <div className="w-full h-96" aria-label="Before and After Comparison Bar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                barSize={60}
              >
                <defs>
                  <linearGradient id="beforeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B6B" stopOpacity={1} />
                    <stop offset="100%" stopColor="#FF4757" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id="afterGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={1} />
                    <stop offset="100%" stopColor="#00D4FF" stopOpacity={0.9} />
                  </linearGradient>
                  <filter id="shadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
                  </filter>
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(255,255,255,0.08)" 
                  vertical={false}
                />
                <XAxis 
                  dataKey="name" 
                  stroke="#E5E7EB" 
                  tick={{ fill: '#E5E7EB', fontSize: 14, fontWeight: 600 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  tickLine={false}
                />
                <YAxis 
                  stroke="#E5E7EB" 
                  tick={{ fill: '#E5E7EB', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(26, 26, 26, 0.95)',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    boxShadow: '0 8px 32px rgba(0, 212, 255, 0.2)',
                  }}
                  labelStyle={{ color: '#F8F9FA', fontWeight: 600, marginBottom: '8px' }}
                  itemStyle={{ color: '#E5E7EB', padding: '4px 0' }}
                  cursor={{ fill: 'rgba(0, 212, 255, 0.05)' }}
                />
                <Bar 
                  dataKey="before" 
                  fill="url(#beforeGradient)" 
                  name="Voor" 
                  radius={[12, 12, 0, 0]}
                  filter="url(#shadow)"
                  animationDuration={1200}
                  animationBegin={0}
                />
                <Bar 
                  dataKey="after" 
                  fill="url(#afterGradient)" 
                  name="Na" 
                  radius={[12, 12, 0, 0]}
                  filter="url(#shadow)"
                  animationDuration={1200}
                  animationBegin={200}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Benefits Comparison Table */}
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-display font-bold text-foreground mb-8 text-center">
            Concrete voordelen in cijfers
          </h3>
          
          <div className="glassmorphic rounded-2xl overflow-hidden shadow-silver-glow border border-primary/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary/20 via-primary/15 to-primary/20 border-b-2 border-primary/30">
                    <th className="px-8 py-5 text-left text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Icon name="ChartBarIcon" size={20} className="text-primary" />
                        <span>Metric</span>
                      </div>
                    </th>
                    <th className="px-8 py-5 text-center text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <span className="inline-flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-red-500/20">
                        <Icon name="XMarkIcon" size={18} className="text-red-400" />
                        <span className="text-red-400">Voor implementatie</span>
                      </span>
                    </th>
                    <th className="px-8 py-5 text-center text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <span className="inline-flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-accent/20">
                        <Icon name="CheckCircleIcon" size={18} className="text-accent" />
                        <span className="text-accent">Na implementatie</span>
                      </span>
                    </th>
                    <th className="px-8 py-5 text-center text-sm font-display font-bold uppercase tracking-wider">
                      <span className="inline-flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r from-success/30 to-success/20">
                        <Icon name="ArrowTrendingUpIcon" size={18} className="text-success" />
                        <span className="text-success">Verbetering</span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {chartData.map((data, index) => {
                    const improvement = data.after > data.before 
                      ? `+${Math.round(((data.after - data.before) / data.before) * 100)}%`
                      : `${Math.round(((data.after - data.before) / data.before) * 100)}%`;
                    const isPositive = data.after > data.before;
                    
                    return (
                      <tr 
                        key={index}
                        className="hover:bg-gradient-to-r hover:from-primary/10 hover:via-primary/5 hover:to-transparent transition-all duration-300 group"
                      >
                        <td className="px-8 py-6 text-sm font-semibold text-foreground bg-gradient-to-r from-card/50 to-transparent">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary/30 to-primary/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <Icon name="ChartBarIcon" size={20} className="text-primary" />
                            </div>
                            <span>{data.name}</span>
                          </div>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-br from-red-500/30 to-red-500/10 text-red-400 text-base font-bold shadow-lg shadow-red-500/20 group-hover:scale-105 transition-transform duration-300">
                            <Icon name="ArrowTrendingDownIcon" size={18} />
                            <span>{data.before.toLocaleString()}</span>
                          </span>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-br from-accent/30 to-accent/10 text-accent text-base font-bold shadow-lg shadow-accent/20 group-hover:scale-105 transition-transform duration-300">
                            <Icon name="ArrowTrendingUpIcon" size={18} />
                            <span>{data.after.toLocaleString()}</span>
                          </span>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className={`inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl text-base font-bold shadow-lg group-hover:scale-110 transition-transform duration-300 ${
                            isPositive 
                              ? 'bg-gradient-to-br from-success/30 to-success/10 text-success shadow-success/20' 
                              : 'bg-gradient-to-br from-red-500/30 to-red-500/10 text-red-400 shadow-red-500/20'
                          }`}>
                            <Icon 
                              name={isPositive ? "ArrowTrendingUpIcon" : "ArrowTrendingDownIcon"} 
                              size={18} 
                            />
                            <span>{improvement}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glassmorphic rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                  <Icon name="TrophyIcon" size={24} className="text-success" />
                </div>
                <h4 className="text-lg font-display font-bold text-foreground">
                  Belangrijkste voordelen
                </h4>
              </div>
              <ul className="space-y-2">
                {metrics.slice(0, 2).map((metric, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{metric.label}:</span> {metric.change}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="glassmorphic rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Icon name="RocketLaunchIcon" size={24} className="text-primary" />
                </div>
                <h4 className="text-lg font-display font-bold text-foreground">
                  Extra impact
                </h4>
              </div>
              <ul className="space-y-2">
                {metrics.slice(2, 4).map((metric, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{metric.label}:</span> {metric.change}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessMetrics;